import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, Plus, Search, TrendingDown, Users, ShieldAlert,
  CheckCircle2, Trash2, RefreshCw, Pencil, Heart, DollarSign, Eye
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = {
  high: { bg: 'bg-red-100 text-red-700', bar: 'bg-red-500', dot: 'bg-red-500' },
  medium: { bg: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500', dot: 'bg-amber-500' },
  low: { bg: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
};

const INTERVENTION_OPTS = ['none', 'planned', 'in_progress', 'completed'];
const OUTCOME_OPTS = ['pending', 'retained', 'churned'];
const RISK_FACTORS = [
  "No purchase in 90+ days", "Support ticket unresolved", "Declined proposal",
  "Negative survey response", "Reduced engagement", "Payment failed",
  "Downgraded plan", "Competitor mention",
];

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function ChurnPredictionPage() {
  const [predictions, setPredictions] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', risk_score: 50, risk_factors: [], last_purchase_date: '', lifetime_value: 0, notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/api/churn/predictions`),
        axios.get(`${API}/api/churn/dashboard`),
      ]);
      setPredictions(p.data.predictions || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name required'); return; }
    try {
      await axios.post(`${API}/api/churn/predictions`, form);
      toast.success('Prediction added!');
      setShowCreate(false);
      setForm({ customer_name: '', customer_email: '', risk_score: 50, risk_factors: [], last_purchase_date: '', lifetime_value: 0, notes: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const updatePrediction = async (id, updates) => {
    try {
      await axios.put(`${API}/api/churn/predictions/${id}`, updates);
      toast.success('Updated');
      fetchData();
      setShowView(null);
    } catch { toast.error('Failed'); }
  };

  const deletePrediction = async (id) => {
    if (!window.confirm('Delete this prediction?')) return;
    try {
      await axios.delete(`${API}/api/churn/predictions/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const toggleFactor = (factor) => {
    setForm(f => ({
      ...f,
      risk_factors: f.risk_factors.includes(factor)
        ? f.risk_factors.filter(r => r !== factor)
        : [...f.risk_factors, factor]
    }));
  };

  const filtered = predictions.filter(p => {
    if (filterRisk !== 'all' && p.risk_level !== filterRisk) return false;
    if (search && !p.customer_name?.toLowerCase().includes(search.toLowerCase()) && !p.customer_email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="churn-prediction-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Churn Prediction<PageInfoTooltip pageKey="churn-prediction" /></h1>
          <p className="text-gray-medium text-sm mt-1">Identify at-risk customers and take proactive action</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={predictions.map(p => ({ customer: p.customer_name, email: p.customer_email, risk: p.risk_level, score: p.risk_score, last_activity: p.last_activity_date }))}
            filename="churn_predictions"
            columns={['customer', 'email', 'risk', 'score', 'last_activity']}
          />
          <Button onClick={() => setShowCreate(true)} data-testid="add-prediction-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Customer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Tracked" value={dashboard.total_tracked || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={AlertTriangle} label="High Risk" value={dashboard.high_risk || 0} color="bg-red-50 text-red-600" />
        <StatCard icon={CheckCircle2} label="Saved" value={dashboard.customers_saved || 0} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Heart} label="Save Rate" value={dashboard.save_rate || 0} color="bg-purple-50 text-purple-600" suffix="%" />
      </div>

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search customers..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="churn-search" />
            </div>
            <Select value={filterRisk} onValueChange={setFilterRisk}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <ShieldAlert className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No churn predictions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(pred => {
                const rc = RISK_COLORS[pred.risk_level] || RISK_COLORS.medium;
                return (
                  <div key={pred.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setShowView(pred)} data-testid={`churn-row-${pred.id}`}>
                    <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", rc.dot)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-text-primary">{pred.customer_name}</span>
                        <Badge className={cn("text-[10px]", rc.bg)}>{pred.risk_level}</Badge>
                        {pred.intervention_status !== 'none' && <Badge className="text-[10px] bg-blue-100 text-blue-700 capitalize">{pred.intervention_status.replace('_', ' ')}</Badge>}
                      </div>
                      <p className="text-xs text-gray-medium truncate">{pred.risk_factors?.slice(0, 2).join(' · ') || 'No factors identified'}</p>
                    </div>
                    <div className="w-24 flex-shrink-0">
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="text-gray-medium">Risk</span>
                        <span className="font-semibold">{pred.risk_score}%</span>
                      </div>
                      <Progress value={pred.risk_score} className="h-1.5" />
                    </div>
                    {pred.lifetime_value > 0 && <span className="text-xs font-medium text-emerald-600 flex-shrink-0">${pred.lifetime_value.toLocaleString()}</span>}
                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={e => { e.stopPropagation(); deletePrediction(pred.id); }}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add At-Risk Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer Name *</Label><Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="churn-customer-name" /></div>
            <div><Label>Email</Label><Input value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} /></div>
            <div><Label>Risk Score (0-100)</Label><Input type="number" min={0} max={100} value={form.risk_score} onChange={e => setForm(f => ({ ...f, risk_score: Number(e.target.value) }))} /></div>
            <div><Label>Lifetime Value ($)</Label><Input type="number" min={0} value={form.lifetime_value} onChange={e => setForm(f => ({ ...f, lifetime_value: Number(e.target.value) }))} /></div>
            <div><Label>Last Purchase Date</Label><Input type="date" value={form.last_purchase_date} onChange={e => setForm(f => ({ ...f, last_purchase_date: e.target.value }))} /></div>
            <div>
              <Label>Risk Factors</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {RISK_FACTORS.map(factor => (
                  <Badge key={factor} className={cn("text-xs cursor-pointer transition-colors", form.risk_factors.includes(factor) ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200")} onClick={() => toggleFactor(factor)}>{factor}</Badge>
                ))}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-prediction-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Update Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{showView?.customer_name}</DialogTitle></DialogHeader>
          {showView && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-medium">Risk Level:</span> <Badge className={cn("text-[10px]", (RISK_COLORS[showView.risk_level] || RISK_COLORS.medium).bg)}>{showView.risk_level}</Badge></div>
                <div><span className="text-gray-medium">Risk Score:</span> <span className="font-semibold">{showView.risk_score}%</span></div>
                <div><span className="text-gray-medium"><Acronym term="LTV" />:</span> <span className="font-medium">${(showView.lifetime_value || 0).toLocaleString()}</span></div>
                <div><span className="text-gray-medium">Last Purchase:</span> <span>{showView.last_purchase_date || '—'}</span></div>
                <div><span className="text-gray-medium">Outcome:</span> <span className="capitalize font-medium">{showView.outcome}</span></div>
              </div>
              {showView.risk_factors?.length > 0 && (
                <div><p className="text-gray-medium mb-1">Risk Factors:</p><div className="flex flex-wrap gap-1">{showView.risk_factors.map(f => <Badge key={f} className="text-[10px] bg-red-50 text-red-600">{f}</Badge>)}</div></div>
              )}
              {showView.notes && <p className="text-gray-medium bg-gray-50 p-2 rounded">{showView.notes}</p>}
              <div className="border-t pt-3 space-y-2">
                <Label className="text-xs font-medium">Update Intervention</Label>
                <div className="flex flex-wrap gap-1.5">
                  {INTERVENTION_OPTS.map(opt => (
                    <Button key={opt} size="sm" variant={showView.intervention_status === opt ? 'default' : 'outline'} className="text-xs capitalize h-7" onClick={() => updatePrediction(showView.id, { intervention_status: opt })}>{opt.replace('_', ' ')}</Button>
                  ))}
                </div>
                <Label className="text-xs font-medium">Set Outcome</Label>
                <div className="flex flex-wrap gap-1.5">
                  {OUTCOME_OPTS.map(opt => (
                    <Button key={opt} size="sm" variant={showView.outcome === opt ? 'default' : 'outline'} className="text-xs capitalize h-7" onClick={() => updatePrediction(showView.id, { outcome: opt })}>{opt}</Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
